import useTranslation from "@babackapp/translations/use-translation";
import { cn } from "@babackapp/ui/lib/utils";
import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isBefore,
  parse,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { ChevronDownIcon } from "lucide-react";
import { useMemo } from "react";

type CurrentOpeningStateProps = {
  openingHours: OpeningHours;
  onClick?: () => void;
};

export default function CurrentOpeningState({
  openingHours,
  onClick,
}: CurrentOpeningStateProps) {
  const {
    isOpen,

    closesSoon,
    opensSoon,

    opensAt,
    closesAt,
  } = useStoreOpeningState(openingHours);
  const t = useTranslation();

  const content = useMemo(() => {
    switch (true) {
      case closesSoon:
        return `${t("settings.shop.physic.openingState.closesSoon")} (${closesAt})`;
      case isOpen:
        return t("settings.shop.physic.openingState.openNow");
      case opensSoon:
        return `${t("settings.shop.physic.openingState.opensSoon")} (${opensAt})`;
      default:
        return t("settings.shop.physic.openingState.closed");
    }
  }, [closesSoon, t, closesAt, isOpen, opensSoon, opensAt]);

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
      className={cn(
        "flex items-center gap-1 text-xs font-medium text-muted-foreground",
        isOpen && "text-green-700",
        closesSoon && "text-yellow-700"
      )}
    >
      {content}
      {onClick && <ChevronDownIcon className="size-4" />}
    </a>
  );
}

type OpeningHoursRange = {
  from: string;
  to: string;
};

export type OpeningHours = {
  monday: Array<OpeningHoursRange>;
  tuesday: Array<OpeningHoursRange>;
  wednesday: Array<OpeningHoursRange>;
  thursday: Array<OpeningHoursRange>;
  friday: Array<OpeningHoursRange>;
  saturday: Array<OpeningHoursRange>;
  sunday: Array<OpeningHoursRange>;
};

export function getDayOpeningHours(openingHours: OpeningHours, date: Date) {
  const day = format(date, "EEEE", {
    locale: enUS,
  }).toLowerCase() as keyof OpeningHours;
  return openingHours[day];
}

export function getNextDay(date: Date) {
  return addDays(date, 1);
}

export type StoreOpeningState = {
  isOpen: boolean;

  closesSoon: boolean;
  opensSoon: boolean;

  closesAt: string | null;
  opensAt: string | null;
};

export function getStoreOpeningState(
  openingHours: OpeningHours
): StoreOpeningState {
  const now = new Date();

  // for testing purposes, update today's time'
  // const now = parse("16:00", "HH:mm", new Date());

  const todayOpeningHours = getDayOpeningHours(openingHours, now);
  const nextDay = getNextDay(now);
  const nextDayOpeningHours = getDayOpeningHours(openingHours, nextDay);

  let isOpen = false;
  let closesSoon = false;
  let opensSoon = false;
  let closesAt: string | null = null;
  let opensAt: string | null = null;

  if (todayOpeningHours) {
    for (const hours of todayOpeningHours) {
      const fromTime = parse(hours.from, "HH:mm", now);
      const toTime = parse(hours.to, "HH:mm", now);

      if (isBefore(fromTime, now) && isAfter(toTime, now)) {
        isOpen = true;
        if (isBefore(toTime, addMinutes(now, 30))) {
          closesSoon = true;
        }
        closesAt = format(toTime, "HH:mm");
      } else if (isAfter(fromTime, now)) {
        if (isBefore(fromTime, addMinutes(now, 30))) {
          opensSoon = true;
        }
        if (!opensAt || isAfter(parse(opensAt, "HH:mm", now), fromTime)) {
          opensAt = format(fromTime, "HH:mm");
        }
      }
    }
  }

  if (!isOpen && nextDayOpeningHours) {
    for (const hours of nextDayOpeningHours) {
      const fromTime = parse(hours.from, "HH:mm", nextDay);
      if (isAfter(fromTime, now)) {
        if (!opensAt || isAfter(parse(opensAt, "HH:mm", now), fromTime)) {
          opensAt = format(fromTime, "HH:mm");
        }
        if (isBefore(fromTime, addMinutes(now, 30))) {
          opensSoon = true;
        }
      }
    }
  }

  return { isOpen, closesSoon, opensSoon, closesAt, opensAt };
}

export function useStoreOpeningState(
  openingHours: OpeningHours
): StoreOpeningState {
  return useMemo(() => {
    return getStoreOpeningState(openingHours);
  }, [openingHours]);
}
